/* Scroll-reveal wrapper: fades + rises a section into place the first
 * time it scrolls into view. Animates transform/opacity only
 * (compositor-friendly, no layout shift) and renders a static element
 * when the user prefers reduced motion.
 *
 * `as` accepts an intrinsic tag name ('div', 'section', 'li', ...);
 * for component children just wrap them — don't pass components to `as`.
 *
 * `amount` defaults to 'some' (fire on the first visible pixel), NOT a
 * numeric threshold: a numeric amount is an IntersectionObserver ratio
 * of the TARGET, so a section taller than 1/amount viewports can never
 * reach it and would stay invisible forever. Only pass a number for
 * elements known to be shorter than the viewport.
 *
 * The reduced branch renders the plain intrinsic tag, not a motion
 * element: zero animation overhead, and no motion visual state that
 * could strand the element at opacity 0 if the two branches ever swap
 * on a live element.
 */
import { motion, useReducedMotion } from 'motion/react';
import { EASE_OUT_QUINT, REVEAL_DURATION, REVEAL_Y } from './motionTokens';

export default function Reveal({
  as = 'div',
  delay = 0,
  y = REVEAL_Y,
  once = true,
  amount = 'some',
  className = '',
  children,
  // Pull motion-only props out of ...props so they can't leak onto the plain
  // DOM tag in the reduced branch (React would warn on unknown attributes).
  // They're forwarded to the motion element, falling back to our defaults.
  initial,
  whileInView,
  viewport,
  transition,
  variants,
  ...props
}) {
  const reduced = useReducedMotion();

  if (reduced) {
    const Tag = as;
    return (
      <Tag className={className} {...props}>
        {children}
      </Tag>
    );
  }

  const Tag = motion[as];
  return (
    <Tag
      className={className}
      initial={initial ?? { opacity: 0, y }}
      whileInView={whileInView ?? { opacity: 1, y: 0 }}
      viewport={viewport ?? { once, amount }}
      transition={transition ?? { duration: REVEAL_DURATION, ease: EASE_OUT_QUINT, delay }}
      variants={variants}
      {...props}
    >
      {children}
    </Tag>
  );
}
